'use client';

import { useCallback, useEffect, useState } from 'react';

export interface RecentTicker {
  ticker: string;
  name: string;
  type?: string;
}

const STORAGE_KEY = 'recentSearches:tickers';
const MAX_RECENT = 5;

function load(): RecentTicker[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((p): p is RecentTicker =>
        typeof p === 'object' && p !== null &&
        typeof (p as RecentTicker).ticker === 'string' &&
        typeof (p as RecentTicker).name === 'string',
      )
      .slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function persist(next: RecentTicker[]): void {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* quota/privacy — ignore */ }
}

export function useRecentTickers() {
  const [recentTickers, setRecentTickers] = useState<RecentTicker[]>([]);

  useEffect(() => {
    setRecentTickers(load());
    // Cross-tab sync: if another tab navigates to a new stock page, pick up
    // the updated list so the navbar/debug-providers dropdowns stay fresh.
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setRecentTickers(load());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const pushRecentTicker = useCallback((entry: RecentTicker) => {
    const up: RecentTicker = { ticker: entry.ticker.toUpperCase(), name: entry.name, type: entry.type };
    setRecentTickers((prev) => {
      const deduped = prev.filter((p) => p.ticker !== up.ticker);
      const next = [up, ...deduped].slice(0, MAX_RECENT);
      persist(next);
      return next;
    });
  }, []);

  const removeRecentTicker = useCallback((ticker: string) => {
    const up = ticker.toUpperCase();
    setRecentTickers((prev) => {
      const next = prev.filter((p) => p.ticker !== up);
      persist(next);
      return next;
    });
  }, []);

  return { recentTickers, pushRecentTicker, removeRecentTicker };
}
